import axios from 'axios';
import { isJwtExpired } from '@utils/jwtUtils';

const PHONE_SYSTEM_API_URL = process.env.REACT_APP_PHONE_SYSTEM_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${PHONE_SYSTEM_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Twilio token helpers
export const setTwilioToken = (token) => {
  if (token) {
    localStorage.setItem('twilioToken', token);
  }
};

export const getTwilioToken = () => {
  return localStorage.getItem('twilioToken');
};

// Request interceptor - add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      // Preempt expired tokens client-side so the user sees a clean redirect
      // instead of a mid-call 401 from the server.
      if (isJwtExpired(token)) {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('twilioToken');
        window.dispatchEvent(new CustomEvent('unauthorized', { detail: { status: 401, reason: 'expired' } }));
        window.location.href = '/login';
        return Promise.reject(new axios.Cancel('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Phone System API Error:', error.response?.data || error.message);
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('twilioToken');
      // Dispatch event for auth context to handle logout
      window.dispatchEvent(new CustomEvent('unauthorized', { detail: { status: 401 } }));
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const login = async (email, password) => {
  console.log('[API] Sending login request...');
  const response = await api.post('/auth/login', {
    email,
    password,
  });
  console.log('[API] Login response received:', response.data);
  console.log('[API] Full payload:', JSON.stringify(response.data, null, 2));
  return response.data;
};

export const logout = async () => {
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('twilioToken');
};

// Call search endpoints
export const searchCalls = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.startDate) params.append('startDate', options.startDate.toISOString());
  if (options.endDate) params.append('endDate', options.endDate.toISOString());
  if (options.phoneNumber) params.append('phoneNumber', options.phoneNumber);
  if (options.callSid) params.append('callSid', options.callSid);
  if (options.patientName) params.append('patientName', options.patientName);
  if (options.coachName) params.append('coachName', options.coachName);
  if (options.category) params.append('category', options.category);
  if (options.dispositionReason) params.append('dispositionReason', options.dispositionReason);
  if (options.page) params.append('page', options.page);
  if (options.pageSize) params.append('pageSize', options.pageSize || 25);
  const response = await api.get(`/calls/search?${params.toString()}`);
  return response.data;
};

export const getCallDetail = async (contactId) => {
  const response = await api.get(`/calls/${contactId}/detail`);
  return response.data;
};

export const getCallFormAttributes = async () => {
  const response = await api.get('/calls/form-attributes');
  return response.data;
};

// Error tracking endpoints
export const getPhoneErrors = async (options = {}) => {
  const params = new URLSearchParams();

  if (options.startDate) params.append('startDate', options.startDate.toISOString());
  if (options.endDate) params.append('endDate', options.endDate.toISOString());
  if (options.category) params.append('category', options.category);
  if (options.severity) params.append('severity', options.severity);
  if (options.callSid) params.append('callSid', options.callSid);
  if (options.page) params.append('page', options.page);
  if (options.pageSize) params.append('pageSize', options.pageSize || 50);
  if (options.includeStatistics !== undefined) params.append('includeStatistics', options.includeStatistics);

  const response = await api.get(`/errors/filter?${params.toString()}`);
  return response.data;
};

export const getPhoneErrorById = async (errorId) => {
  const response = await api.get(`/errors/${errorId}`);
  return response.data;
};

export const getPhoneErrorStatistics = async (options = {}) => {
  const params = new URLSearchParams();

  if (options.startDate) params.append('startDate', options.startDate.toISOString());
  if (options.endDate) params.append('endDate', options.endDate.toISOString());

  const response = await api.get(`/errors/statistics?${params.toString()}`);
  return response.data;
};

export const getCallErrorHistory = async (callSid) => {
  const response = await api.get(`/errors/call/${callSid}`);
  return response.data;
};

// Hang up an active call via the backend (terminates via Twilio REST API + records end time).
export const hangUpCall = async (callSid) => {
  const response = await api.post('/twilio/outgoing/hang-up', { callSid });
  return response.data;
};

// Voicemail endpoints
export const sendToVoicemail = async (callSid) => {
  const response = await api.post('/twilio/outgoing/send-to-voicemail', { callSid });
  return response.data;
};

// IVR call initiation
export const initiateIvrCall = async ({ patientGuid, phoneNumber }) => {
  const response = await api.post('/twilio/ivr/resupply/initiate', { patientGuid, phoneNumber });
  return response.data;
};

// Team queue monitoring
export const getTeamQueues = async () => {
  const response = await api.get('/teams/queues');
  return response.data;
};

// Answer the next queued caller in a specific team's queue.
// Coach identity is read from the JWT by the backend — only the target queue name is sent.
// The backend validates the coach is eligible to answer that queue type.
export const answerQueueCall = async ({ queueName }) => {
  const response = await api.post('/queue/answer', { queueName });
  return response.data;
};

// Voice template management
export const getVoiceTemplates = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.templateKey) params.append('templateKey', options.templateKey);
  if (options.companyId) params.append('companyId', options.companyId);
  const query = params.toString();
  const response = await api.get(`/voice-templates${query ? `?${query}` : ''}`);
  return response.data;
};

export const createVoiceTemplate = async (template) => {
  const response = await api.post('/voice-templates', template);
  return response.data;
};

export const updateVoiceTemplate = async (id, template) => {
  const response = await api.put(`/voice-templates/${id}`, template);
  return response.data;
};

export const deleteVoiceTemplate = async (id) => {
  const response = await api.delete(`/voice-templates/${id}`);
  return response.data;
};

export const previewVoiceTemplate = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.templateKey) params.append('templateKey', options.templateKey);
  if (options.language) params.append('language', options.language);
  if (options.companyId) params.append('companyId', options.companyId);
  if (options.patientTypeId) params.append('patientTypeId', options.patientTypeId);
  if (options.companyName) params.append('companyName', options.companyName);
  const response = await api.get(`/voice-templates/preview?${params.toString()}`);
  return response.data;
};

// IVR flow diagram
export const getIvrFlowDefinition = async () => {
  const response = await api.get('/ivr/flow-definition');
  return response.data;
};

// IVR question management
export const getIvrQuestions = async () => {
  const response = await api.get('/ivr/questions');
  return response.data;
};

export const updateIvrQuestion = async (id, data) => {
  const response = await api.put(`/ivr/questions/${id}`, data);
  return response.data;
};

export const getIvrQuestionOverrides = async (questionId) => {
  const response = await api.get(`/ivr/questions/${questionId}/overrides`);
  return response.data;
};

export const createIvrQuestionOverride = async (questionId, data) => {
  const response = await api.post(`/ivr/questions/${questionId}/overrides`, data);
  return response.data;
};

export const updateIvrQuestionOverride = async (questionId, overrideId, data) => {
  const response = await api.put(`/ivr/questions/${questionId}/overrides/${overrideId}`, data);
  return response.data;
};

export const deleteIvrQuestionOverride = async (questionId, overrideId) => {
  const response = await api.delete(`/ivr/questions/${questionId}/overrides/${overrideId}`);
  return response.data;
};

export default api;
